import type { PublicAvailabilitySlot } from "@emr/contracts";
import { type QueryKey, type UseQueryResult, useQueries } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  type AvailabilityAnswer,
  type DateRange,
  type LoadedSearch,
  loadSearch,
  searchesBetween,
  searchesFor,
  slotsByDay,
} from "./availabilityCalendar";

/** What the picker knows about the month on screen. */
export interface CalendarAvailability {
  /** The clinic's time zone as the searches named it; undefined until one answers. */
  readonly timeZone: string | undefined;
  /** The times of every day the answered searches cover, by local date; days without times are absent. */
  readonly days: ReadonlyMap<string, readonly PublicAvailabilitySlot[]>;
  /** Whether a day's search has answered for it, so a day without times really has none. */
  readonly answered: (date: string) => boolean;
  /**
   * Whether a day's search answered but stopped before the day at the store's cap (after its
   * follow-ups), so the day's times are not known.
   */
  readonly unread: (date: string) => boolean;
  /** The physicians a manage link's times name (none on the booking page). */
  readonly providers: AvailabilityAnswer["providers"];
  /** A search for the month on screen has not answered yet. */
  readonly pending: boolean;
  readonly failed: boolean;
  readonly error: unknown;
  readonly refetch: () => void;
}

interface SearchesState {
  readonly answers: readonly (LoadedSearch | undefined)[];
  readonly pending: boolean;
  readonly error: unknown;
  readonly refetch: () => void;
}

/** Stable, so useQueries keeps its combined result until a search changes. */
function combineSearches(results: UseQueryResult<LoadedSearch>[]): SearchesState {
  return {
    answers: results.map((result) => result.data),
    pending: results.some((result) => result.isPending),
    error: results.find((result) => result.isError)?.error ?? null,
    refetch: () => {
      for (const result of results) void result.refetch();
    },
  };
}

/**
 * The availability of the month on screen, in as few requests as the contract allows
 * (availabilityCalendar.ts): the fixed searches that cover the month, each cached by react-query
 * under `queryKey` plus its dates. `keep` drops times the patient must not be offered (on a
 * manage link, the booking's own time). `exact` says when each time in an answer is one of the
 * store's rows, so only a full answer can have been cut short (loadSearch).
 */
export function useAvailabilityCalendar({
  queryKey,
  load,
  month,
  selected,
  anchor,
  firstDay,
  lastDay,
  enabled,
  retry,
  keep,
  exact,
}: {
  readonly queryKey: QueryKey;
  readonly load: (range: DateRange, signal: AbortSignal) => Promise<AvailabilityAnswer>;
  readonly month: string;
  /** The chosen day ("" for none): its times stay loaded while another month is on screen. */
  readonly selected: string;
  /** The first day of the first search; later searches follow it, 63 days at a time. */
  readonly anchor: string;
  /** The first and last days a patient may book, on the clinic's calendar. */
  readonly firstDay: string;
  readonly lastDay: string;
  readonly enabled: boolean;
  readonly retry?: boolean | number;
  readonly keep?: (slot: PublicAvailabilitySlot) => boolean;
  readonly exact?: (answer: AvailabilityAnswer) => boolean;
}): CalendarAvailability {
  const searches = useMemo(() => {
    const shown = searchesFor(month, anchor, firstDay, lastDay);
    const chosen =
      selected === "" ? [] : searchesBetween(selected, selected, anchor, firstDay, lastDay);
    return [
      ...shown,
      ...chosen.filter((range) => !shown.some((other) => other.fromDate === range.fromDate)),
    ];
  }, [month, selected, anchor, firstDay, lastDay]);
  const state = useQueries({
    queries: searches.map((range) => ({
      queryKey: [...queryKey, range.fromDate, range.toDate],
      queryFn: ({ signal }: { readonly signal: AbortSignal }) =>
        loadSearch(load, range, signal, exact === undefined ? {} : { exact }),
      enabled,
      ...(retry === undefined ? {} : { retry }),
    })),
    combine: combineSearches,
  });

  const { answers } = state;
  const result = useMemo(() => {
    const days = new Map<string, PublicAvailabilitySlot[]>();
    // The days each answer speaks for, and the whole range each answered search asked about.
    const answeredRanges: DateRange[] = [];
    const searchedRanges: DateRange[] = [];
    let timeZone: string | undefined;
    const providers = new Map<string, NonNullable<AvailabilityAnswer["providers"]>[number]>();
    let named = false;
    answers.forEach((answer, index) => {
      const range = searches[index];
      if (answer === undefined || range === undefined) return;
      const throughDate = answer.throughDate < range.toDate ? answer.throughDate : range.toDate;
      searchedRanges.push(range);
      answeredRanges.push({ fromDate: range.fromDate, toDate: throughDate });
      timeZone ??= answer.timeZone;
      if (answer.providers !== undefined) named = true;
      for (const provider of answer.providers ?? []) providers.set(provider.id, provider);
      const kept = keep === undefined ? answer.slots : answer.slots.filter(keep);
      for (const [date, slots] of slotsByDay(kept, answer.timeZone)) {
        // Each search speaks for its own days only, so neighbouring spans never double a day.
        if (date < range.fromDate || date > throughDate) continue;
        days.set(date, [...(days.get(date) ?? []), ...slots]);
      }
    });
    const within = (ranges: readonly DateRange[], date: string) =>
      ranges.some((range) => range.fromDate <= date && date <= range.toDate);
    const answered = (date: string) => within(answeredRanges, date);
    return {
      timeZone,
      days,
      answered,
      unread: (date: string) => !answered(date) && within(searchedRanges, date),
      providers: named ? [...providers.values()] : undefined,
    };
  }, [answers, searches, keep]);

  return {
    ...result,
    pending: state.pending,
    failed: state.error !== null,
    error: state.error,
    refetch: state.refetch,
  };
}
